import type {Spec} from 'immutability-helper';

import type {
  FromServerTemplate,
  FromServerTemplateVersion,
  Template,
  TemplateCollection,
  TemplateCollectionSummary,
} from 'notebook/entry-templates/TemplateConstants';
import type {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import type {CidIdMap, CidNotePartProvenanceIdMap} from 'notebook/notes/NoteConstants';
import type {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import type {MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';
import type {EntryNote} from 'notebook/util/NoteUtilConstants';
import type {Response} from 'notifications';

export type TemplateRevertOp = Spec<Template> & {
  templateVersion?: {
    entryVersion?: {
      $merge?: {
        structuredTableMappings?: Array<StructuredTableMapping>;
        note?: EntryNote;
      };
    };
  };
};

export const TEMPLATE_EVENT_PAYLOADS = {
  TEMPLATE_CLOSE: {} as {templateId: string},
  TEMPLATE_DELETE: {} as {templateId: string},
  TEMPLATE_DELETE_COMPLETED: {} as {templateId: string},
  TEMPLATE_DELETE_FAILED: {} as {templateId: string; error: Response},
  TEMPLATE_FETCH_VERSIONS: {} as {templateId: string},
  TEMPLATE_FETCH_VERSIONS_COMPLETED: {} as {
    templateId: string;
    versions: Array<FromServerTemplateVersion>;
    parent?: {
      name: string;
      id: string;
      versionId: number;
      modifiedAt: string;
    } | null;
  },
  TEMPLATE_FETCH_VERSIONS_FAILED: {} as {templateId: string},
  TEMPLATE_LOAD_VERSION: {} as {templateId: string; versionId: number},
  TEMPLATE_LOAD_VERSION_COMPLETED: {} as {
    templateId: string;
    versionId: number;
    version: FromServerTemplate;
  },
  TEMPLATE_LOAD_VERSION_FAILED: {} as {templateId: string; versionId: number},
  TEMPLATE_OPEN: {} as {templateId: string},
  TEMPLATE_OPEN_COMPLETED: {} as {
    templateId: string;
    template: FromServerTemplate;
    templateCollection: TemplateCollection;
  },
  TEMPLATE_OPEN_FAILED: {} as {templateId: string; error: Response},
  TEMPLATE_REGISTER_OBJECTS: {} as {templateId: string; cidIdMap: CidIdMap},
  TEMPLATE_RESET_TO_OPEN_VERSION: {} as {templateId: string},
  TEMPLATE_REVERT: {} as {
    templateId: string;
    op: TemplateRevertOp;
    restoreData: SelectionRestoreData | null;
    multidocTableOp: MultidocTableOpMap;
  },
  TEMPLATE_SAVE: {} as {templateId: string},
  TEMPLATE_SAVE_COMPLETED: {} as {
    templateId: string;
    savedVersion: number;
    versionId: number;
    cidIdMap: CidIdMap;
    cidNotePartProvenanceIdMap?: CidNotePartProvenanceIdMap;
    serverData: {
      editURL: string;
      templateCollection: TemplateCollectionSummary;
    };
  },
  TEMPLATE_SAVE_FAILED: {} as {templateId: string; error: Response},
  TEMPLATE_UPDATE: {} as {
    templateId: string;
    op: Spec<Template>;
    cidIdMap: CidIdMap;
    bumpVersion: boolean;
    updatedItemPartIndexes?: Array<number>;
    canIncrementalSave?: boolean;
    performTableEvaluation?: boolean;
    multidocTableOp?: MultidocTableOpMap;
  },
} as const;

export type TemplateEventPayload = typeof TEMPLATE_EVENT_PAYLOADS;
